import { Archive, CircleCheck, CircleDot, Link2, List, Pencil, Plus, RefreshCw, Save, Search, Send, Tags, X } from 'lucide-react'

import { QueryState } from '@krp/sale-ui'
import { Button, CheckboxField, ChoiceChips, InfoTip, TextField } from '@krp/ui-kit'

import type { FinderMode, OwnPriceKindsActions, OwnPriceKindsVm, OwnPriceOwner } from '../model/types'
import { CommandError, Problem } from './parts'
import './own-nomenclature.css'
import { ICON_SIZE, ICON_SIZE_SMALL } from '../../../ownIcons'

const FINDER_MODES: readonly { id: FinderMode; label: string }[] = [
  { id: 'sku', label: 'Артикул' },
  { id: 'barcode', label: 'Штрихкод' },
]

const OWNERS: readonly { id: OwnPriceOwner; label: string }[] = [
  { id: 'own', label: 'Своя база' },
  { id: 'source', label: 'Другой источник' },
]

/**
 * «Цены» → вкладка своей базы: виды цен (розничная, оптовая…) — создать, переименовать, убрать в архив; цены конкретного товара —
 * по артикулу или штрихкоду, окном «Цены товара» (на дату и вся история). Валюту вида с записанными ценами служба сменить не
 * даст: цены хранятся без валюты, она — у вида.
 */
export function OwnPriceKindsScreen({ vm, actions }: { vm: OwnPriceKindsVm; actions: OwnPriceKindsActions }) {
  const write = vm.writeDisabledReason === null
  const form = vm.form
  const finder = vm.finder
  return (
    <section className="own-screen" aria-labelledby="own-price-kinds-title">
      <header className="own-toolbar">
        <h2 id="own-price-kinds-title">Цены своей базы</h2>
        <Button variant="ghost" icon={<List size={ICON_SIZE} aria-hidden="true" />} onClick={actions.openProducts}>
          Товары
        </Button>
        <Button variant="ghost" icon={<RefreshCw size={ICON_SIZE} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
          Обновить
        </Button>
      </header>

      <section className="own-section" aria-labelledby="own-price-finder-title">
        <h3 id="own-price-finder-title">Цены товара</h3>
        <div className="own-row" role="search">
          <ChoiceChips
            label="Искать по"
            value={finder.mode}
            items={FINDER_MODES}
            onChange={(mode) => actions.finderChanged(finder.text, mode === 'barcode' ? 'barcode' : 'sku')}
          />
          <TextField
            label={finder.mode === 'sku' ? 'Артикул целиком' : 'Штрихкод целиком'}
            type="search"
            value={finder.text}
            onChange={(text) => actions.finderChanged(text, finder.mode)}
          />
          <Button variant="primary" icon={<Search size={ICON_SIZE} aria-hidden="true" />} disabled={finder.text.trim() === ''} onClick={actions.find}>
            Найти
          </Button>
        </div>
        {finder.searched && <QueryState query={finder.query} emptyText="Такого товара в своей базе нет" onRetry={actions.find} what="Поиск" />}
        {finder.rows.length > 0 && (
          <ul className="own-list" aria-label="Найденные товары">
            {finder.rows.map((row) => (
              <li key={row.id} data-archived={row.archived}>
                <span>{row.name}</span>
                {row.archived && (
                  <span className="own-badge">
                    <Archive size={ICON_SIZE_SMALL} aria-hidden="true" />в архиве
                  </span>
                )}
                <span className="own-list__meta">
                  {row.skuText} · {row.barcodesText}
                </span>
                <Button icon={<Tags size={ICON_SIZE} aria-hidden="true" />} onClick={() => actions.openPrices(row.id)}>
                  Цены товара
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="own-section" aria-labelledby="own-price-kinds-list-title">
        <div className="own-section__head">
          <h3 id="own-price-kinds-list-title">
            Виды цен <InfoTip label="Виды цен">Вид цены — розничная, оптовая и любые другие. Синхронизация с 1С сопоставляет виды цен двух баз картой справочника в «Синхронизации».</InfoTip>
          </h3>
          {form === null && (
            <Button variant="primary" icon={<Plus size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={actions.create}>
              Новый вид цены
            </Button>
          )}
        </div>
        {vm.writeDisabledReason !== null && (
          <p className="own-muted" role="note">
            Создание и правка выключены. {vm.writeDisabledReason}
          </p>
        )}
        {vm.done !== null && (
          <p className="own-problem" role="status">
            <CircleCheck size={ICON_SIZE_SMALL} aria-hidden="true" />
            {vm.done}
          </p>
        )}
        {form !== null && (
          <div className="own-section" aria-label={form.draft.id === null ? 'Новый вид цены' : 'Изменить вид цены'}>
            <div className="own-section__head">
              <h3>{form.draft.id === null ? 'Новый вид цены' : 'Изменить вид цены'}</h3>
              <Button variant="ghost" icon={<X size={ICON_SIZE} aria-hidden="true" />} onClick={actions.closeForm}>
                Закрыть
              </Button>
            </div>
            <div className="own-row">
              <TextField label="Название" value={form.draft.name} onChange={(name) => actions.draftChanged({ ...form.draft, name })} />
              <TextField
                label="Валюта (ISO 4217)"
                value={form.draft.currency}
                onChange={(currency) => actions.draftChanged({ ...form.draft, currency: currency.toUpperCase() })}
              />
            </div>
            <ChoiceChips
              label="Хозяин цены"
              value={form.draft.owner}
              items={OWNERS}
              onChange={(owner) => actions.draftChanged({ ...form.draft, owner: owner === 'source' ? 'source' : 'own' })}
            />
            <p className="own-muted" role="note">
              {form.draft.owner === 'own'
                ? 'Цены этого вида ведутся здесь: хозяин — своя база.'
                : 'Цены этого вида ведутся в другом источнике (например закупочная в 1С): своя база их не раздаёт.'}
            </p>
            {form.draft.id !== null && (
              <CheckboxField
                label="В архиве (новые цены этого вида не записываются)"
                checked={form.draft.archived}
                onChange={(archived) => actions.draftChanged({ ...form.draft, archived })}
              />
            )}
            <Problem text={form.problem} />
            {form.error !== null && <CommandError title="Вид цены не записан" error={form.error} />}
            <div className="own-row">
              <Button
                variant="primary"
                icon={<Save size={ICON_SIZE} aria-hidden="true" />}
                busy={form.saving}
                disabled={!write || form.problem !== null}
                onClick={actions.save}
              >
                {form.draft.id === null ? 'Создать' : 'Сохранить'}
              </Button>
            </div>
          </div>
        )}
        <QueryState query={vm.query} emptyText="Видов цен пока нет — создайте, например, «Розничная»" onRetry={actions.reload} what="Виды цен" />
        {vm.rows.length > 0 && (
          <table className="own-table">
            <caption className="own-muted">Видов цен: {vm.rows.length}</caption>
            <thead>
              <tr>
                <th>Название</th>
                <th>Валюта</th>
                <th>Хозяин цены</th>
                <th>Состояние</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {vm.rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{row.currency}</td>
                  <td>
                    <span className="own-badge" data-kind={row.owner === 'own' ? 'owner-own' : 'owner-source'}>
                      {row.owner === 'own' ? <Send size={ICON_SIZE_SMALL} aria-hidden="true" /> : <Link2 size={ICON_SIZE_SMALL} aria-hidden="true" />}
                      {row.owner === 'own' ? 'своя база' : 'другой источник'}
                    </span>
                  </td>
                  <td>
                    <span className="own-badge" data-kind={row.archived ? 'archived' : 'current'}>
                      {row.archived ? <Archive size={ICON_SIZE_SMALL} aria-hidden="true" /> : <CircleDot size={ICON_SIZE_SMALL} aria-hidden="true" />}
                      {row.archived ? 'в архиве' : 'действует'}
                    </span>
                  </td>
                  <td>
                    <Button variant="ghost" icon={<Pencil size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={() => actions.edit(row.id)}>
                      Изменить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </section>
  )
}
